import { useState, useRef, useEffect } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppHeader } from '../../components/Layout/AppHeader';
import { BlockAutoUpdate } from '../../components/BlockAutoUpdate/BlockAutoUpdate';
import { AuthImage } from '../../components/AuthImage/AuthImage';
import { PhotoViewer } from '../../components/PhotoViewer/PhotoViewer';
import { useAuthImage } from '../../hooks/useAuthImage';
import { createDetail, editDetail, uploadImages, deleteImage, validateContainer, getImages, readOcrField } from '../../services/api';
import { addPendingDetail, updatePendingDetailFields, updateCachedCargoDetails, getPendingDetailByDetailId, deletePendingDetail, getPhotoDraft, putPhotoDraft, deletePhotoDraft, findCachedPositionByCargo } from '../../services/db';
import { invalidateSwApiCache } from '../../services/swCache';
import { notifyPendingChanged, triggerOfflineFlush } from '../../hooks/useOfflineSync';
import { isOfflineForUser, useNetworkStatus, isPoorConnection } from '../../services/networkStatus';
import { track } from '../../services/analytics';
import { captureError } from '../../services/errorTracking';
import { useAppPreferences, useFeatures } from '../../hooks/useFeatures';
import { getSupplierStamp } from '../../utils/supplierStamp';
import { buildMetadata } from '../../utils/metadata';
import type { Detail, ApiImage, Position } from '../../types';
import { displayProductNames, productDraftOf } from '../../services/selfManaged';
import { DeliveryNoteSection } from '../../components/DeliveryNoteSection/DeliveryNoteSection';
import { DraftProduct } from '../../components/DraftProduct/DraftProduct';
import './EditDetailPage.css';

const MAX_PHOTOS = 15;

// Estrategia para "guardar fotos": SOLO iPhone/iPad usa el share sheet, que
// es su única puerta web a la app Fotos ("Guardar imágenes", operación local).
// TODO lo demás (Android, Windows, Mac, Linux) DESCARGA los ficheros:
//  - Android: el share sheet no ofrece galería local — el destino de imágenes
//    es Google Fotos (SUBIDA a la nube: sin cobertura no guarda nada). La
//    descarga cae en Descargas, MediaStore la indexa y aparece en la galería,
//    sin tocar la red.
//  - Escritorio (Windows…): el share sheet ofrecía WhatsApp y demás en vez de
//    dejar elegir carpeta. La descarga guarda el fichero directamente.
const IS_IOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
const IS_ANDROID = /android/i.test(navigator.userAgent);

const CONTAINER_TYPES: { id: number; label: string }[] = [
  { id: 1, label: "40'HC" },
  { id: 2, label: "40'DRY" },
  { id: 3, label: "40'MAXI" },
  { id: 4, label: "20'MAXI" },
  { id: 5, label: "20'DRY" },
];

const EMPTY_DETAIL: Partial<Detail> = {
  containerNumber: '',
  idContainerType: 1,
  numberPlat: '',
  trailerNumberPlat: '',
  sealNumber: '',
  sealVeter: '',
  // Campos numéricos: no defaultear a 0 para que el usuario vea inputs vacíos
  // y pueda teclear su valor sin pelearse con un cero fijo.
  checkIn: '',
  checkOut: '',
  comments: '',
  lotNumber: '',
  location: '',
};

// ── Utilidades ────────────────────────────────────────────────────────────
function base64ToBlob(base64: string, mime = 'image/jpeg'): Blob {
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

// Respaldo local ANTES de subir: solo tratamos fotos NUEVAS, cuyos bytes ya
// están en memoria (imageBase64). Sin red — funciona en modo avión. Las fotos
// ya subidas al servidor no se re-descargan.
function newPhotoToBlob(p: ApiImage): Blob | null {
  return p.imageBase64 ? base64ToBlob(p.imageBase64, 'image/jpeg') : null;
}

// Descarga local foto a foto (Android + escritorio).
//  - revoke DIFERIDO (~10s): revocar el object URL inmediatamente puede
//    abortar la descarga en algunos Chrome.
//  - ~300ms entre descargas: Chrome pide una única vez el permiso de
//    "descargas múltiples"; aceptado, el resto encadena sin fricción.
async function downloadPhotosLocally(photos: ApiImage[]): Promise<void> {
  for (const p of photos) {
    const blob = newPhotoToBlob(p);
    if (!blob) continue;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = p.imageName || 'foto.jpg';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
    await new Promise((r) => setTimeout(r, 300));
  }
}

// Genera un nombre único para evitar la deduplicación del backend, que descarta
// uploads cuyo FileName ya exista para el mismo idCargoDetail (ver BlobImageRepository.SaveMultiPartImage).
function uniquePhotoName(original?: string): string {
  const safe = (original ?? '').replace(/[^\w.-]+/g, '_');
  const dot = safe.lastIndexOf('.');
  const base = dot > 0 ? safe.slice(0, dot) : safe || 'photo';
  const ext = dot > 0 ? safe.slice(dot) : '.jpg';
  const stamp = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 8);
  return `${base}_${stamp}${rand}${ext}`;
}

function compressImage(dataUrl: string, maxDim = 1600, quality = 0.75): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (Math.max(width, height) > maxDim) {
        const ratio = maxDim / Math.max(width, height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
      const result = canvas.toDataURL('image/jpeg', quality);
      resolve(result && result.length > 'data:,'.length ? result : dataUrl);
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

// La metadata del dispositivo y red vive en utils/metadata.ts: la comparten
// esta pantalla (edición en primer plano) y el Service Worker (reintentos de
// la cola), y su regla de EditTs es la misma en los dos.

// Normalización de nombres de foto para dedup: el nombre local (imageName)
// casa con el campo `fileName` de las respuestas del servidor — en ellas
// `imageName` es el GUID generado por el backend y nunca coincide.
function normPhotoName(name?: string): string {
  return (name ?? '').trim().toLowerCase();
}

// ¿Fallo de subida que la cola offline puede resolver reintentando? Sin
// respuesta del servidor (timeout, red caída, lie-fi, cancelación por pasar a
// modo offline) o respuesta de indisponibilidad (5xx, 408, 429). Un 4xx
// distinto es un rechazo del contenido: reintentarlo no lo arreglaría.
function isTransientUploadFailure(err: unknown): boolean {
  const ax = err as { isAxiosError?: boolean; response?: { status?: number } } | null | undefined;
  if (!ax?.isAxiosError) return false;
  const status = ax.response?.status;
  if (status == null) return true;
  return status >= 500 || status === 408 || status === 429;
}

// ── OCR state machine ──────────────────────────────────────────────────────
type OcrState =
  | { step: 'container-choice' }
  | { step: 'source-choice'; field: keyof Detail; fieldType: string; reminder?: string }
  | { step: 'uploading'; field: keyof Detail; fieldType: string; photoUrl: string; photoName: string; photoBase64: string }
  | { step: 'result'; field: keyof Detail; fieldType: string; photoUrl: string; photoName: string; photoBase64: string; normalizedText: string; candidates: string[]; editedText: string };

// ── OCR trigger button ─────────────────────────────────────────────────────
function OcrBtn({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="ocr-btn" onClick={onClick}>
      <svg viewBox="0 0 24 24" fill="currentColor" width="13" height="13">
        <path d="M9.5 6.5v3h-3v-3h3M11 5H5v6h6V5zm-1.5 9.5v3h-3v-3h3M11 13H5v6h6v-6zm6.5-6.5v3h-3v-3h3M23 5h-6v6h6V5zM23 13h-1.5v1.5H23V13zm-1.5 1.5H20v1.5h1.5V14.5zm1.5 1.5h-1.5v1.5H23V16zm-3 1.5h-1.5V19H20v-1.5zm1.5 1.5H20v1.5h1.5V19zm1.5 1.5h-1.5V22H23v-1.5zM20 20h-1.5v1.5H20V20zm1.5 1.5H20V23h1.5v-1.5z"/>
      </svg>
      OCR
    </button>
  );
}

// ── OCR modal (bottom sheet) ───────────────────────────────────────────────
function OcrModal({
  ocr,
  onClose,
  onContainerChoice,
  onSourcePick,
  onRepeat,
  onAccept,
  onEditText,
}: {
  ocr: OcrState;
  onClose: () => void;
  onContainerChoice: (type: 'container_front_code' | 'container_inside_code') => void;
  onSourcePick: (src: 'camera' | 'gallery') => void;
  onRepeat: () => void;
  onAccept: () => void;
  onEditText: (text: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="ocr-overlay" onClick={onClose}>
      <div className="ocr-modal" onClick={(e) => e.stopPropagation()}>
        <button className="ocr-modal__close" type="button" onClick={onClose}>
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>

        {ocr.step === 'container-choice' && (
          <>
            <h3 className="ocr-modal__title">{t('editDetail.ocr.readContainer')}</h3>
            <p className="ocr-modal__subtitle">{t('editDetail.ocr.whereNumber')}</p>
            <div className="ocr-choice-btns">
              <button type="button" className="ocr-choice-btn" onClick={() => onContainerChoice('container_front_code')}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="26" height="26">
                  <path d="M20 8H4V6h16v2zm-2-6H6v2h12V2zm4 10v8c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2v-8c0-1.1.9-2 2-2h16c1.1 0 2 .9 2 2zm-6 4l-6-3.27v6.53L16 16z"/>
                </svg>
                {t('editDetail.ocr.doorExterior')}
              </button>
              <button type="button" className="ocr-choice-btn" onClick={() => onContainerChoice('container_inside_code')}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="26" height="26">
                  <path d="M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7z"/>
                </svg>
                {t('editDetail.ocr.interior')}
                <span className="ocr-choice-hint">{t('editDetail.ocr.interiorHint')}</span>
              </button>
            </div>
          </>
        )}

        {ocr.step === 'source-choice' && (
          <>
            <h3 className="ocr-modal__title">{t('editDetail.ocr.selectImage')}</h3>
            {ocr.reminder && (
              <div className="ocr-reminder">
                <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" style={{ flexShrink: 0 }}>
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                </svg>
                {ocr.reminder}
              </div>
            )}
            <div className="ocr-source-btns">
              <button type="button" className="ocr-source-btn" onClick={() => onSourcePick('camera')}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="30" height="30">
                  <path d="M12 15.2c1.769 0 3.2-1.431 3.2-3.2S13.769 8.8 12 8.8 8.8 10.231 8.8 12s1.431 3.2 3.2 3.2zM9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9z"/>
                </svg>
                {t('editDetail.ocr.camera')}
              </button>
              <button type="button" className="ocr-source-btn" onClick={() => onSourcePick('gallery')}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="30" height="30">
                  <path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z"/>
                </svg>
                {t('editDetail.ocr.gallery')}
              </button>
            </div>
          </>
        )}

        {(ocr.step === 'uploading' || ocr.step === 'result') && (
          <>
            <div className="ocr-photo-wrap">
              <img src={ocr.photoUrl} alt={t('editDetail.ocr.photoAlt')} className="ocr-photo" />
            </div>

            {ocr.step === 'uploading' && (
              <div className="ocr-processing">
                <div className="spinner spinner--green" />
                <span>{t('editDetail.ocr.processing')}</span>
              </div>
            )}

            {ocr.step === 'result' && (
              <>
                <div className="ocr-result">
                  <label className="ocr-result__label">{t('editDetail.ocr.detectedText')}</label>
                  <input
                    className="ocr-result__input"
                    value={ocr.editedText}
                    onChange={(e) => onEditText(e.target.value.toUpperCase())}
                    autoCapitalize="characters"
                    spellCheck={false}
                  />
                  {ocr.candidates.length > 1 && (
                    <div className="ocr-candidates">
                      <label className="ocr-result__label">{t('editDetail.ocr.altCandidates')}</label>
                      <select
                        className="ocr-candidates__select"
                        value=""
                        onChange={(e) => { if (e.target.value) onEditText(e.target.value); e.target.value = ''; }}
                      >
                        <option value="">{t('editDetail.ocr.selectCandidate')}</option>
                        {ocr.candidates.map((c, i) => (
                          <option key={i} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                <div className="ocr-actions">
                  <button type="button" className="ocr-action-btn ocr-action-btn--repeat" onClick={onRepeat}>
                    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                      <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
                    </svg>
                    {t('editDetail.ocr.repeat')}
                  </button>
                  <button type="button" className="ocr-action-btn ocr-action-btn--accept" onClick={onAccept}>
                    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                    {t('editDetail.ocr.accept')}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Wrapper del PhotoViewer que resuelve la URL real:
// - data:/blob: → directa
// - resto (SAS o token) → vía useAuthImage
function PhotoLightbox({ url, onClose }: { url: string; onClose: () => void }) {
  const isLocal = url.startsWith('data:') || url.startsWith('blob:');
  const authed = useAuthImage(isLocal ? undefined : url);
  const src = isLocal ? url : authed;
  if (!src) return null;
  return <PhotoViewer src={src} onClose={onClose} />;
}

// ── Página principal ───────────────────────────────────────────────────────
export function EditDetailPage() {
  const { idCargo, idDetail } = useParams<{ idCargo: string; idDetail: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isNew = idDetail === 'new';

  const existingDetail: Detail | undefined = location.state?.detail;
  // La Position llega por history.state desde la lista. Si no viene (app
  // relanzada sobre esta URL, pestana restaurada), se recupera de la cache
  // por idCargo: sin sus ids maestros un detalle nuevo no puede guardarse.
  const [position, setPosition] = useState<Position | undefined>(location.state?.position);
  useEffect(() => {
    if (position || !idCargo) return;
    let cancelled = false;
    findCachedPositionByCargo(Number(idCargo))
      .then((p) => { if (!cancelled && p) setPosition(p); })
      .catch(() => { /* sin cache: el guardado lo registra si faltan los ids */ });
    return () => { cancelled = true; };
  }, [position, idCargo]);

  // Edición de un item NEW que sigue en cola sin id de servidor: en
  // DetailPage le asignamos idCargoDetail = -pendingId. Aquí lo detectamos
  // para (a) cargar las fotos directamente desde el pending sin llamar API,
  // (b) guardar actualizando la entrada de la cola en lugar del backend.
  const isEditingPendingNew = !isNew && (
    (existingDetail?.idCargoDetail ?? Number(idDetail)) <= 0
  );
  const pendingIdFromUrl = isEditingPendingNew
    ? -(existingDetail?.idCargoDetail ?? Number(idDetail))
    : 0;

  const [form, setForm] = useState<Partial<Detail>>(() => {
    if (isNew) {
      const now = new Date();
      const hh = String(now.getHours()).padStart(2, '0');
      const mm = String(now.getMinutes()).padStart(2, '0');
      return { ...EMPTY_DETAIL, comments: `${hh}:${mm}` };
    }
    // Detalle existente: tratamos los 0 guardados en BBDD como "no rellenado"
    // para que los inputs queden vacíos y el usuario pueda teclear sin borrar.
    const d: Partial<Detail> = { ...existingDetail };
    (['packageNumber', 'humidity', 'tare', 'netWeight', 'vgm'] as const).forEach((k) => {
      if (d[k] === 0) d[k] = undefined;
    });
    return d;
  });

  const [serverPhotos, setServerPhotos] = useState<ApiImage[]>([]);
  // Si estamos editando un NEW pendiente, sus fotos viven en
  // existingDetail.listNewImages (el pending las arrastra desde que se
  // encoló). Las cargamos como newPhotos para que el usuario pueda añadir
  // o quitar más igual que en un detalle nuevo.
  const [newPhotos, setNewPhotos] = useState<ApiImage[]>(() =>
    isEditingPendingNew ? (existingDetail?.listNewImages ?? []) : []
  );
  // Si esta carga existente tiene un pending EDIT en cola con fotos añadidas
  // offline, lo localizamos al montar para (a) precargar sus fotos en
  // newPhotos y que el usuario las vea/edite, (b) borrar el pending tras un
  // guardado online exitoso y evitar que la sync re-suba las mismas fotos.
  const [existingEditPendingId, setExistingEditPendingId] = useState<number | null>(null);
  // Fotos del servidor pendientes de eliminar al pulsar Guardar.
  // Se mantienen aparte de serverPhotos (que solo refleja la UI) para no
  // perderlas al filtrar la cuadrícula tras la selección.
  const [pendingServerDeletes, setPendingServerDeletes] = useState<ApiImage[]>([]);
  // No hay nada que cargar del servidor para un detalle nuevo o un NEW
  // pendiente (sus fotos están todas locales).
  const [loadingPhotos, setLoadingPhotos] = useState(!isNew && !isEditingPendingNew);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedNew, setSelectedNew] = useState<Set<number>>(new Set());
  const [selectedServer, setSelectedServer] = useState<Set<number>>(new Set());

  const toggleSelectNew = (idx: number) => {
    setSelectedNew((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };
  const toggleSelectServer = (idx: number) => {
    setSelectedServer((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };
  const totalSelected = selectedNew.size + selectedServer.size;
  const deleteSelected = () => {
    const serverToQueue = serverPhotos.filter((_, i) => selectedServer.has(i));
    setPendingServerDeletes((prev) => [...prev, ...serverToQueue]);
    setNewPhotos((prev) => prev.filter((_, i) => !selectedNew.has(i)));
    setServerPhotos((prev) => prev.filter((_, i) => !selectedServer.has(i)));
    setSelectedNew(new Set());
    setSelectedServer(new Set());
    setSelectMode(false);
  };
  const cancelSelectMode = () => {
    setSelectMode(false);
    setSelectedNew(new Set());
    setSelectedServer(new Set());
  };

  const prefs = useAppPreferences();
  const features = useFeatures();
  const supplierStamp = !isNew && features.supplierStamp ? getSupplierStamp(form, i18n.language) : null;
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [containerValid, setContainerValid] = useState<boolean | null>(null);
  const [toast, setToast] = useState('');
  const [limitMsg, setLimitMsg] = useState('');
  const [galleryPromptOpen, setGalleryPromptOpen] = useState(false);
  // Resultado del intento de guardado, para decidir qué hacer cuando se
  // cierre el modal de galería: navegar atrás (éxito/offline-pending) o
  // mostrar el error y dejar al usuario en la pantalla.
  const [saveResult, setSaveResult] = useState<
    | { kind: 'success' }
    | { kind: 'offlinePending'; message?: string }
    | { kind: 'error'; message: string }
    | null
  >(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const containerNumberRef = useRef<HTMLInputElement>(null);

  // ── OCR ────────────────────────────────────────────────────────────────
  // `isOnline` se deriva del estado de red unificado: cubre tanto el offline
  // real (`!navigator.onLine`) como el offline manual elegido por el usuario.
  // De esta forma los botones OCR y la validación de contenedor desaparecen
  // mientras el usuario está en "Trabajar offline" y reaparecen al volver
  // a online.
  const netStatus = useNetworkStatus();
  const isOnline = netStatus.kind !== 'offline' && netStatus.kind !== 'manual_offline';
  const [ocr, setOcr] = useState<OcrState | null>(null);
  const ocrCtxRef = useRef<{ field: keyof Detail; fieldType: string } | null>(null);
  const ocrCameraRef = useRef<HTMLInputElement>(null);
  const ocrGalleryRef = useRef<HTMLInputElement>(null);

  const startOcr = (field: keyof Detail, fieldType?: string) => {
    if (field === 'containerNumber') {
      setOcr({ step: 'container-choice' });
    } else {
      ocrCtxRef.current = { field, fieldType: fieldType! };
      setOcr({ step: 'source-choice', field, fieldType: fieldType! });
    }
  };

  const handleContainerChoice = (fieldType: 'container_front_code' | 'container_inside_code') => {
    const reminder = fieldType === 'container_inside_code'
      ? t('editDetail.ocr.interiorReminder')
      : undefined;
    ocrCtxRef.current = { field: 'containerNumber', fieldType };
    setOcr({ step: 'source-choice', field: 'containerNumber', fieldType, reminder });
  };

  const handleOcrSourcePick = (source: 'camera' | 'gallery') => {
    if (source === 'camera') ocrCameraRef.current?.click();
    else ocrGalleryRef.current?.click();
  };

  const handleOcrFilePick = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !ocrCtxRef.current) return;

    const { field, fieldType } = ocrCtxRef.current;
    const reader = new FileReader();

    reader.onload = async () => {
      const rawDataUrl = reader.result as string;
      const dataUrl = await compressImage(rawDataUrl);
      const base64 = dataUrl.split(',')[1];

      setOcr({ step: 'uploading', field, fieldType, photoUrl: dataUrl, photoName: file.name, photoBase64: base64 });

      try {
        const fd = new FormData();
        fd.append('image', file);
        fd.append('fieldType', fieldType);
        fd.append('idCargoDetail', String(existingDetail?.idCargoDetail ?? 0));
        fd.append('deviceSource', 'PWA');

        const res = await readOcrField(fd);
        const result = res.data;

        setOcr({
          step: 'result',
          field,
          fieldType,
          photoUrl: dataUrl,
          photoName: file.name,
          photoBase64: base64,
          normalizedText: result.normalizedText,
          candidates: result.candidates,
          editedText: result.normalizedText,
        });
      } catch (e) {
        captureError(e, { where: 'EditDetailPage.readOcrField', fieldType });
        track({ name: 'ocr_used', params: { field_type: fieldType, result: 'failed' } });
        setOcr(null);
        setToast(t('editDetail.ocrError'));
      }
    };
    reader.readAsDataURL(file);
  };

  const handleOcrAccept = () => {
    if (!ocr || ocr.step !== 'result') return;
    track({ name: 'ocr_used', params: { field_type: ocr.fieldType, result: 'accepted' } });
    set(ocr.field, ocr.editedText);
    // Derivar base64 igual que handlePhotoPick para garantizar coherencia
    const comma = ocr.photoUrl.indexOf(',');
    const base64 = comma !== -1 ? ocr.photoUrl.slice(comma + 1) : ocr.photoBase64;
    setNewPhotos((prev) => [
      ...prev,
      {
        idCargoDetail: existingDetail?.idCargoDetail ?? 0,
        imageName: uniquePhotoName(ocr.photoName || 'ocr-photo.jpg'),
        urlImage: ocr.photoUrl,
        imageBase64: base64,
        fromApi: false,
        dateUpload: new Date().toISOString(),
        ocrSource: ocr.fieldType,
      },
    ]);
    setOcr(null);
    ocrCtxRef.current = null;
  };

  const handleOcrRepeat = () => {
    if (!ocr || ocr.step !== 'result') return;
    ocrCtxRef.current = { field: ocr.field, fieldType: ocr.fieldType };
    setOcr({ step: 'source-choice', field: ocr.field, fieldType: ocr.fieldType });
  };

  const handleOcrTextChange = (text: string) => {
    setOcr((prev) => {
      if (!prev || prev.step !== 'result') return prev;
      return { ...prev, editedText: text };
    });
  };

  const handleOcrClose = () => {
    setOcr(null);
    ocrCtxRef.current = null;
  };
  // ── fin OCR ────────────────────────────────────────────────────────────

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    if (isNew || isEditingPendingNew) return;
    const photoDetailId = existingDetail?.idCargoDetail ?? Number(idDetail);
    if (!photoDetailId || photoDetailId <= 0) return;
    setLoadingPhotos(true);
    getImages(photoDetailId)
      .then((r) => {
        const data = r.data;
        const list: ApiImage[] = Array.isArray(data)
          ? data
          : (data as { listDataImage?: ApiImage[] })?.listDataImage ?? [];
        setServerPhotos(list);
      })
      .catch((e) => {
        captureError(e, { where: 'EditDetailPage.getImages', idDetail });
        setServerPhotos([]);
      })
      .finally(() => setLoadingPhotos(false));
  }, [isNew, isEditingPendingNew, existingDetail?.idCargoDetail, idDetail]);

  // Para una carga existente, si hay un pending EDIT en cola con fotos
  // añadidas offline, las precargamos como newPhotos para que el usuario las
  // pueda ver/eliminar/ampliar. Guardamos el id del pending para limpiarlo
  // tras un guardado online con éxito (evita duplicados en la siguiente sync).
  useEffect(() => {
    if (isNew || isEditingPendingNew) return;
    const photoDetailId = existingDetail?.idCargoDetail ?? Number(idDetail);
    if (!photoDetailId || photoDetailId <= 0) return;
    let cancelled = false;
    getPendingDetailByDetailId(photoDetailId)
      .then((pending) => {
        if (cancelled || !pending) return;
        setExistingEditPendingId(pending.id);
        const list = (pending.detail?.listNewImages ?? []) as ApiImage[];
        if (list.length > 0) setNewPhotos(list);
      })
      .catch((e) => {
        captureError(e, { where: 'EditDetailPage.loadPending', idDetail });
      });
    return () => { cancelled = true; };
  }, [isNew, isEditingPendingNew, existingDetail?.idCargoDetail, idDetail]);

  // ── Borrador de fotos: persistencia AL CAPTURAR ─────────────────────────
  // Clave estable por carga/detalle:
  //  - EDIT existente → idCargo:idCargoDetail
  //  - edición de un NEW encolado → idCargo:p<pendingId>
  //  - NEW → idCargo:new — determinista, sobrevive a reabrir. DECISIÓN del
  //    caso "nueva carga abierta dos veces": ambas aperturas comparten
  //    borrador; la segunda restaura las fotos de la primera (sesgo
  //    pro-recuperación) y el guardado con éxito lo limpia.
  // LECCIÓN ANDROID (bug de campo): la clave se captura UNA SOLA VEZ al montar
  // la pantalla, en un ref — NUNCA se recalcula. El guardado muta identidades
  // (el create asigna idCargoDetail, el estado cambia) y una clave recalculada
  // haría que la limpieza final borrara la clave NUEVA (vacía) dejando huérfano
  // el borrador bajo la clave con la que se persistió — que la siguiente "carga
  // nueva" del cargo restauraría. Persistencia, restauración y limpieza usan
  // TODAS esta misma captura.
  // useState con inicializador perezoso, no useRef: el valor se calcula en el
  // primer render y React garantiza que no se recalcula nunca más, que es
  // justo la captura única que necesitamos.
  const [draftKey] = useState<string>(() =>
    isEditingPendingNew && pendingIdFromUrl > 0
      ? `${idCargo}:p${pendingIdFromUrl}`
      : isNew
        ? `${idCargo}:new`
        : `${idCargo}:${Number(idDetail) || existingDetail?.idCargoDetail || 0}`
  );

  const draftReadyRef = useRef(false);
  const persistAskedRef = useRef(false);
  // CERROJO del borrador (bug de campo: borrador resucitando tras guardar). La
  // limpieza corre en el async del submit, pero newPhotos sigue vivo en
  // pantalla — cualquier render posterior con mutación de fotos re-escribía el
  // borrador completo DESPUÉS del delete. Con el cerrojo echado (fotos
  // declaradas a salvo: subidas o encoladas), el efecto persistidor no vuelve a
  // escribir jamás en esta instancia. Se libera solo si un guardado fracasa con
  // fotos sin encolar (el borrador debe seguir vivo).
  const draftClosedRef = useRef(false);

  // Restauración al abrir: merge con dedup por nombre contra lo que ya haya
  // (fotos de la cola cargadas por los efectos de arriba). La dedup contra
  // SERVIDOR la hace el efecto de reconciliación de abajo.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const draft = await getPhotoDraft(draftKey);
        if (cancelled || !draft || !Array.isArray(draft.photos) || draft.photos.length === 0) return;
        const photos = draft.photos as ApiImage[];
        setNewPhotos((prev) => {
          const have = new Set(prev.map((p) => normPhotoName(p.imageName)));
          const add = photos.filter((p) => p.imageName && !have.has(normPhotoName(p.imageName)));
          return add.length > 0 ? [...prev, ...add] : prev;
        });
      } catch { /* sin borrador o IDB caída: la pantalla sigue */ }
      finally {
        // Habilita la persistencia SOLO tras intentar restaurar — si no, el
        // primer render (lista vacía) pisaría el borrador existente.
        if (!cancelled) draftReadyRef.current = true;
      }
    })();
    return () => { cancelled = true; };
  }, [draftKey]);

  // Persistencia en CADA mutación de fotos (añadir/borrar) — jamás "al salir":
  // la muerte de la pestaña no avisa. Quitar la última foto también persiste
  // (borra el borrador). storage.persist() se pide con la primera foto para que
  // el navegador no evicte IDB.
  useEffect(() => {
    // Cerrojo echado = fotos a salvo: prohibido volver a escribir (ni siquiera
    // se crea la transacción — cubre también writes encolados).
    if (!draftReadyRef.current || draftClosedRef.current) return;
    // Con catch: era la única escritura a IndexedDB de la app sin recoger. Si
    // el navegador mataba la transacción (pestaña congelada con la pantalla
    // apagada), salía como promesa no capturada y nadie sabía que el borrador
    // no se había guardado. La capa de BD ya reintenta una vez; si aun así
    // falla, queda registrado con el número de fotos que estaban en juego.
    putPhotoDraft(draftKey, Number(idCargo), newPhotos).catch((err) => {
      captureError(err, { where: 'EditDetailPage.putPhotoDraft', idCargo, photoCount: newPhotos.length });
    });
    if (newPhotos.length > 0 && !persistAskedRef.current) {
      persistAskedRef.current = true;
      try { void navigator.storage?.persist?.(); } catch { /* sin soporte */ }
    }
  }, [newPhotos, draftKey, idCargo]);

  // Reconciliación contra servidor: si una foto local ya CONSTA (match por
  // fileName del GET — el nombre original del multipart), quitarla NO es
  // pérdida: ya llegó (p. ej. crash entre subirla y limpiar el borrador).
  useEffect(() => {
    if (serverPhotos.length === 0) return;
    const onServer = new Set(
      serverPhotos.map((p) => normPhotoName(p.fileName)).filter((n) => n.length > 0)
    );
    if (onServer.size === 0) return;
    // No es un cascading render evitable: la lista del servidor solo llega tras
    // una respuesta de red, y este set se salta a sí mismo si no hay nada que
    // reconciliar (devuelve `prev` idéntico).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setNewPhotos((prev) => {
      const kept = prev.filter((p) => !onServer.has(normPhotoName(p.imageName)));
      return kept.length === prev.length ? prev : kept;
    });
  }, [serverPhotos]);

  // beforeunload SOLO mientras hay un submit en curso: las fotos aún no están
  // encoladas (solo en borrador) y un F5 cortaría la subida a mitad. El
  // navegador muestra su diálogo genérico; si el usuario cancela, el submit
  // sigue intacto. Se desactiva al terminar (saving=false) — fuera del submit
  // el refresh es seguro: el borrador salva las fotos y la cola se reanuda
  // sola.
  useEffect(() => {
    if (!saving) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Chrome exige returnValue para mostrar el diálogo.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [saving]);
  // ── fin borrador de fotos ───────────────────────────────────────────────

  const set = (field: keyof Detail, value: string | number | undefined) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  // Para inputs numéricos: si el usuario borra el contenido, guardamos undefined
  // (no 0). Así el input queda vacío en pantalla y permite teclear el valor real
  // sin tener que pelearse con un 0 fijo.
  const setNum = (field: keyof Detail) => (e: ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    set(field, v === '' ? undefined : Number(v));
  };

  const refreshTime = () => {
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    set('comments', `${hh}:${mm}`);
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    // El proveedor rellena lo que tenga: para él nada es obligatorio.
    if (features.containerNumberRequired && !form.containerNumber?.trim()) errs.containerNumber = t('editDetail.containerRequired');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleContainerBlur = async () => {
    if (!form.containerNumber) return;
    try {
      const res = await validateContainer(form.containerNumber);
      setContainerValid(res.data === true);
    } catch (e) {
      captureError(e, { where: 'EditDetailPage.validateContainer.blur' });
      setContainerValid(null);
    }
  };

  const handlePhotoPick = (e: ChangeEvent<HTMLInputElement>) => {
    const totalPhotos = serverPhotos.length + newPhotos.length;
    if (totalPhotos >= MAX_PHOTOS) {
      setLimitMsg(t('editDetail.limitMax', { max: MAX_PHOTOS }));
      e.target.value = '';
      return;
    }
    const incoming = Array.from(e.target.files ?? []);
    const remaining = MAX_PHOTOS - totalPhotos;
    if (incoming.length > remaining) {
      setLimitMsg(t('editDetail.limitRemaining', { count: remaining, max: MAX_PHOTOS }));
    }
    const files = incoming.slice(0, remaining);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = async () => {
        const compressed = await compressImage(reader.result as string);
        setNewPhotos((prev) => [...prev, {
          idCargoDetail: existingDetail?.idCargoDetail ?? 0,
          imageName: uniquePhotoName(file.name),
          urlImage: compressed,
          imageBase64: compressed.split(',')[1],
          fromApi: false,
          dateUpload: new Date().toISOString(),
        }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removeNewPhoto = (idx: number) =>
    setNewPhotos((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      // Scroll al primer campo con error y foco para que el usuario lo vea.
      const el = containerNumberRef.current;
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Pequeño delay para que el focus no rompa el smooth scroll en algunos navegadores.
        setTimeout(() => el.focus({ preventScroll: true }), 350);
      }
      return;
    }

    // Activamos el estado de loading ya, antes de la primera llamada al
    // backend (validación de contenedor), para que el botón refleje
    // inmediatamente que algo está pasando y el usuario no vuelva a pulsar.
    setSaving(true);

    // ── Presupuesto de tiempo ADAPTATIVO del guardado en primer plano ───
    // Decisión de producto: 25s de "Guardando" es demasiada espera cuando la
    // red ya se sabe mala. Reparto (espejo proporcional del patrón Android
    // verificación→envío→recuperación):
    //  - Conexión BUENA → 25s total (4s validación + 17s datos + fotos).
    //  - Conexión MALA (isPoorConnection: 2g/slow-2g, downlink <1Mbps o
    //    latencias reales medias ≥3s) → 10s total (2s + 6s + resto).
    // Agotado → encolar lo pendiente + liberar UI + volver a la lista. El caso
    // "timeout con red viva" también encola: la barrera pre-create de la cola
    // absorbe el posible "el servidor sí procesó".
    const poorConnection = isPoorConnection();
    const SUBMIT_BUDGET_MS = poorConnection ? 10_000 : 25_000;
    const VALIDATE_TIMEOUT_MS = poorConnection ? 2_000 : 4_000;
    const DATA_SEND_TIMEOUT_MS = poorConnection ? 6_000 : 17_000;

    // Clave del borrador con la que se PERSISTIÓ (lección Android): la limpieza
    // final debe borrar esa, no la que resultaría del estado post-guardado —
    // el create asigna un idCargoDetail que cambiaría la clave calculada.
    const submitDraftKey = draftKey;

    // ── 0. Detección offline temprana (espejo de shouldQueueWrites Android) ──
    // "Offline" aquí cubre tanto sin red real como modo offline manual del
    // usuario (cuando decidió trabajar offline por red lenta). En ambos
    // casos encolamos en lugar de intentar la API.
    const startedOffline = isOfflineForUser();
    track({
      name: 'detail_create_attempt',
      params: { online: !startedOffline, photo_count: newPhotos.length },
    });

    // ── 1. Validar número de contenedor (si hay red) ────────────────────
    if (isOnline && !startedOffline && form.containerNumber) {
      let valid: boolean | null = null;
      try {
        const res = await validateContainer(form.containerNumber, { timeout: VALIDATE_TIMEOUT_MS });
        valid = res.data === true;
        setContainerValid(valid);
      } catch (e) {
        captureError(e, { where: 'EditDetailPage.validateContainer.submit' });
        valid = null;
      }
      if (valid === false) {
        const ok = confirm(t('editDetail.confirmInvalidContainer', { container: form.containerNumber }));
        if (!ok) {
          setSaving(false);
          return;
        }
      }
    }

    // El reloj del presupuesto arranca AQUÍ, tras la validación de contenedor
    // (que puede incluir un confirm() esperando al usuario — su tiempo pensando
    // no es tiempo de red y no debe comerse el presupuesto).
    const submitStartedAt = Date.now();
    const budgetLeftMs = () => SUBMIT_BUDGET_MS - (Date.now() - submitStartedAt);

    // Borrados de fotos de servidor. INVARIANTE de nombre: estas entradas
    // vienen de serverPhotos (GetImagesToAppController) y su imageName es el
    // GUID DEL SERVIDOR — que es lo que el backend exige para borrar (espejo
    // inverso del lío fileName/imageName de la dedup: dedup por fileName,
    // borrado por imageName). Dos caminos de aplicación:
    //  - ONLINE: endpoint dedicado DeleteImage, foto a foto (más abajo).
    //  - OFFLINE/encolado: viajan como listDeleteImages DENTRO del item — el
    //    EDIT del backend las procesa antes de guardar (CargoDetailController,
    //    EditCargoDetailAppController). Sin esto, el borrado offline era un
    //    no-op silencioso que devolvía éxito.
    const photosToDelete = pendingServerDeletes;
    // Excluir campos de imágenes del form para que editDetail no afecte las fotos existentes
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { listNewImages: _ln, listDeleteImages: _ld, ...formFields } = form as Detail;
    // Al crear, los IDs maestros (Product, OtherProduct, Carrier, etc.) los
    // arrastramos desde la Position de la que partimos, porque el form no los
    // pide al usuario y el backend los necesita (FK constraints en Cargo/Detail).
    // Al editar, formFields ya los trae del existingDetail.
    let masterIds: Partial<Pick<Position, 'idProduct' | 'idOtherProduct' | 'idCarrier' | 'idPlanning'>> = {};
    if (isNew) {
      // Ultimo recurso si la Position no llego ni se recupero al montar.
      const pos = position ?? (await findCachedPositionByCargo(Number(idCargo)).catch(() => null)) ?? undefined;
      if (pos) {
        masterIds = {
          idProduct: pos.idProduct,
          idOtherProduct: pos.idOtherProduct,
          idCarrier: pos.idCarrier,
          idPlanning: pos.idPlanning,
        };
      } else {
        // Sin ids maestros el servidor rechazaba el detalle (FK) y el item
        // quedaba atascado. Desde sep 2026 el servidor los completa desde la
        // carga, asi que se envia igual, pero queda registrado por si vuelve.
        captureError(new Error('Detalle nuevo sin Position: ids maestros ausentes'), {
          where: 'EditDetailPage.masterIds',
          idCargo,
          startedOffline,
        });
      }
    }
    const payload = { ...masterIds, ...formFields, metadata: buildMetadata() };

    // Resultado del intento de guardar — lo decidimos en el try/catch y
    // luego, al final, decidimos si abrir modal de galería o no. Las fotos
    // locales han de quedar siempre disponibles para el usuario.
    let outcome:
      | { kind: 'success' }
      | { kind: 'offlinePending'; message?: string }
      | { kind: 'error'; message: string }
      | null = null;
    // true si alguna foto acabó SIN subir NI encolar (fallo de subida suelto,
    // encolado del remanente fallido): en ese caso el borrador se conserva —
    // esas fotos no están en ningún otro sitio.
    let unsavedPhotoFailures = false;

    // ── DEADLINE DURO externo (garantía estructural anti-cuelgue) ───────
    // El presupuesto interno acota cada petición, pero un cuelgue fuera de esos
    // puntos (p. ej. el bucle de borrado de fotos del servidor, sin presupuesto
    // propio) dejaba el botón en "Guardando" infinito. Este watchdog es
    // independiente del flujo: si el submit ENTERO no ha terminado al agotarse
    // presupuesto+margen, encola TODO lo pendiente, limpia el borrador (fotos a
    // salvo en cola), libera la UI y NAVEGA a la lista — el controller nunca se
    // queda mirando el botón (paridad Android). Las operaciones que sigan en
    // vuelo son inocuas: barrera pre-create y dedup por fileName absorben sus
    // efectos tardíos.
    const submitAbandoned = { current: false };
    const HARD_DEADLINE_MS = SUBMIT_BUDGET_MS + 5_000;
    const watchdog = setTimeout(() => {
      void (async () => {
        submitAbandoned.current = true;
        track({ name: 'submit_hard_deadline', params: { photo_count: newPhotos.length, poor_connection: poorConnection } });
        try {
          const editId = isNew ? 0 : (Number(idDetail) || existingDetail?.idCargoDetail || 0);
          await addPendingDetail(
            Number(idCargo),
            isNew ? 'NEW' : 'EDIT',
            { ...payload, idCargoDetail: editId, listNewImages: newPhotos, listDeleteImages: photosToDelete } as Detail
          );
          notifyPendingChanged();
          // Fotos a salvo en cola → cerrojo + limpieza del borrador.
          draftClosedRef.current = true;
          try { await deletePhotoDraft(submitDraftKey); } catch { /* best-effort */ }
          if (isNew && submitDraftKey !== `${idCargo}:new`) {
            try { await deletePhotoDraft(`${idCargo}:new`); } catch { /* best-effort */ }
          }
          triggerOfflineFlush(true);
          setSaving(false);
          alert(t('editDetail.hardDeadlineQueued'));
          navigate(-1);
        } catch (err) {
          captureError(err, { where: 'EditDetailPage.hardDeadline', idCargo });
          setSaving(false);
          alert(t('editDetail.hardDeadlineFailed'));
        }
      })();
    }, HARD_DEADLINE_MS);

    // ── Atajo edición de NEW pendiente: actualizamos la entrada en cola y
    // dejamos que el SW la sincronice cuando haya red. No llamamos a la API
    // porque el detalle aún no existe en servidor.
    if (isEditingPendingNew && pendingIdFromUrl > 0) {
      try {
        await updatePendingDetailFields(pendingIdFromUrl, {
          ...formFields,
          listNewImages: newPhotos,
          metadata: buildMetadata(),
        });
        notifyPendingChanged();
        // Si hay red real Y el usuario no eligió modo offline, intentamos
        // un flush inmediato — el SW reintentará la creación con los nuevos
        // campos. Si está en modo offline manual, dejamos en cola sin
        // intentar (el ping de recuperación lo procesará al volver).
        if (!isOfflineForUser()) triggerOfflineFlush(true);
        outcome = isOfflineForUser()
          ? { kind: 'offlinePending' }
          : { kind: 'success' };
      } catch (e) {
        captureError(e, { where: 'EditDetailPage.updatePendingNew', pendingId: pendingIdFromUrl });
        outcome = { kind: 'error', message: t('editDetail.errorSaveOffline') };
      }
    }

    // ── Atajo offline: si el navegador ya sabe que no hay red, encolamos
    // directamente sin intentar la API. Equivalente a shouldQueueWrites en
    // EditDetailViewModel.kt (Android). Evita timeouts largos en iOS Safari.
    if (!outcome && startedOffline) {
      try {
        const editId = isNew ? 0 : (Number(idDetail) || existingDetail?.idCargoDetail || 0);
        await addPendingDetail(
          Number(idCargo),
          isNew ? 'NEW' : 'EDIT',
          { ...payload, idCargoDetail: editId, listNewImages: newPhotos, listDeleteImages: photosToDelete } as Detail
        );
        notifyPendingChanged();
        track({ name: 'detail_create_offline_queued', params: { photo_count: newPhotos.length } });
        outcome = { kind: 'offlinePending' };
      } catch (e) {
        captureError(e, { where: 'EditDetailPage.addPendingDetail.startedOffline', idCargo, isNew });
        outcome = { kind: 'error', message: t('editDetail.errorSaveLocal') };
      }
    }

    if (!outcome) {
    try {
      // Timeout de datos acotado por el presupuesto (con suelo de 5s para no
      // fabricar timeouts imposibles si llegamos aquí con poco margen).
      const dataTimeout = { timeout: Math.max(5_000, Math.min(DATA_SEND_TIMEOUT_MS, budgetLeftMs())) };
      let detailId: number;
      if (isNew) {
        const res = await createDetail(Number(idCargo), payload, dataTimeout);
        detailId = (res.data as { idCargoDetail?: number })?.idCargoDetail ?? 0;
        // Defensivo: 2xx sin idCargoDetail = FALLO. Sin esto, el flujo seguía
        // con outcome de éxito pero saltándose las fotos en silencio.
        if (!detailId) throw new Error('Respuesta 2xx del create sin idCargoDetail');
      } else {
        // Usar idDetail de la URL como fuente principal (más fiable que location.state)
        const editId = Number(idDetail) || existingDetail?.idCargoDetail || 0;
        const editRes = await editDetail(editId, payload, dataTimeout);
        // Defensivo: 2xx sin el Detail esperado = FALLO.
        const editBody = editRes.data as { idCargoDetail?: number } | null | undefined;
        if (!editBody || typeof editBody !== 'object' || !(Number(editBody.idCargoDetail) > 0)) {
          throw new Error('Respuesta 2xx de la edición sin el detalle esperado');
        }
        detailId = editId;
      }

      // Borrar fotos seleccionadas via endpoint dedicado (DELETE /api/Blob/DeleteImage/...)
      if (photosToDelete.length > 0 && detailId) {
        const delErrors: string[] = [];
        for (const p of photosToDelete) {
          try {
            await deleteImage(detailId, p.imageName);
          } catch (delErr: unknown) {
            captureError(delErr, { where: 'EditDetailPage.deleteImage', detailId, imageName: p.imageName });
            const e = delErr as { response?: { status?: number; data?: unknown }; message?: string };
            const status = e.response?.status ?? '';
            const msg = JSON.stringify(e.response?.data ?? e.message ?? delErr);
            delErrors.push(`${p.imageName || 'foto'} (${status}): ${msg}`);
          }
        }
        if (delErrors.length > 0 && !submitAbandoned.current) {
          alert(t('editDetail.errorDeletePhotos', { count: delErrors.length, errors: delErrors.join('\n') }));
        }
      }

      // Subir fotos una por una; errores no bloquean el guardado del detalle
      let queuedPhotoRemainder = 0;
      if (newPhotos.length > 0 && detailId) {
        const photoErrors: string[] = [];
        // Índice desde el que las fotos pasan a la cola offline (-1 = ninguna).
        // Dos motivos, mismo remedio:
        //  - Presupuesto agotado: la UI se libera y la cola las sube sola.
        //  - Fallo TRANSITORIO de una subida (timeout, red caída, 5xx): antes
        //    esa foto acababa en un alert "fallaron 1 foto(s): timeout of
        //    13380ms exceeded" y solo sobrevivía en el borrador local — el
        //    controller tenía que volver a abrir la carga y guardar a mano.
        //    Ahora esa foto y las siguientes se encolan: con la red así, las
        //    que faltan fallarían igual y la cola sí sabe esperar y reintentar.
        // INVARIANTE: una foto EN VUELO cuando expira el presupuesto (o cuya
        // petición agota el timeout) es segura tanto si su request aborta como
        // si llegó a completarse en el servidor: el remanente encolado la
        // re-subiría, y la dedup por FileName del backend + el pre-filtro por
        // lectura de la cola la absorben sin duplicar.
        let deferFrom = -1;
        let deferReason: 'budget' | 'network' = 'budget';
        for (let pi = 0; pi < newPhotos.length; pi++) {
          const p = newPhotos[pi];
          if (budgetLeftMs() <= 0) {
            deferFrom = pi;
            deferReason = 'budget';
            break;
          }
          try {
            let rawBlob: Blob;
            try {
              rawBlob = await (await fetch(p.urlImage)).blob();
              if (rawBlob.size === 0) throw new Error('blob vacío');
            } catch {
              rawBlob = base64ToBlob(p.imageBase64 ?? '', 'image/jpeg');
            }
            // Subida online: NO comprimimos para preservar la calidad
            // original. La compresión está reservada al flujo offline
            // donde la red es el cuello de botella real.
            const blob = new Blob([await rawBlob.arrayBuffer()], { type: 'image/jpg' });
            const fd = new FormData();
            fd.append('photos', blob, p.imageName || 'photo.jpg');
            // Cada foto respeta lo que quede de presupuesto (suelo de 5s).
            await uploadImages(detailId, fd, { timeout: Math.max(5_000, budgetLeftMs()) });
            track({ name: 'photo_uploaded', params: { size_kb: Math.round(blob.size / 1024) } });
          } catch (photoErr: unknown) {
            const e = photoErr as { response?: { status?: number; data?: unknown }; message?: string };
            const status = e.response?.status ?? '';
            track({
              name: 'photo_upload_failed',
              params: { size_kb: 0, status_code: status || 'network' },
            });
            if (isTransientUploadFailure(photoErr)) {
              captureError(photoErr, { where: 'EditDetailPage.uploadImage', detailId, imageName: p.imageName, encolada: true });
              deferFrom = pi;
              deferReason = 'network';
              break;
            }
            captureError(photoErr, { where: 'EditDetailPage.uploadImage', detailId, imageName: p.imageName });
            const msg = JSON.stringify(e.response?.data ?? e.message ?? photoErr);
            photoErrors.push(`${p.imageName || 'foto'} (${status}): ${msg}`);
            unsavedPhotoFailures = true;
          }
        }
        if (deferFrom >= 0) {
          const remainingPhotos = newPhotos.slice(deferFrom);
          try {
            await addPendingDetail(Number(idCargo), 'EDIT', {
              ...payload,
              idCargoDetail: detailId,
              listNewImages: remainingPhotos,
            } as Detail);
            notifyPendingChanged();
            queuedPhotoRemainder = remainingPhotos.length;
            track({
              name: deferReason === 'budget' ? 'submit_budget_queued' : 'submit_photo_failure_queued',
              params: { photos_left: remainingPhotos.length },
            });
          } catch (e) {
            captureError(e, { where: 'EditDetailPage.queuePhotoRemainder', idCargo, detailId, deferReason });
            photoErrors.push(`${remainingPhotos.length} foto(s): no se pudieron dejar pendientes`);
            unsavedPhotoFailures = true;
          }
        }
        if (photoErrors.length > 0 && !submitAbandoned.current) {
          alert(t('editDetail.errorUploadPhotos', { count: photoErrors.length, errors: photoErrors.join('\n') }));
        }
      }

      track({
        name: isNew ? 'detail_create_success' : 'detail_edit_success',
        params: { online: true },
      });

      // Espejar el cambio en la caché de details para que la vista offline
      // refleje la creación / edición sin esperar al próximo fetch. En NEW
      // insertamos el item con el id devuelto por el servidor; en EDIT
      // actualizamos campos del item existente.
      if (detailId) {
        await updateCachedCargoDetails(Number(idCargo), (list) => {
          if (isNew) {
            return [...list, { ...payload, idCargoDetail: detailId } as Detail];
          }
          return list.map((d) =>
            d.idCargoDetail === detailId ? { ...d, ...payload, idCargoDetail: detailId } as Detail : d
          );
        }).catch((e) => captureError(e, { where: 'EditDetailPage.updateCachedCargoDetails', idCargo, detailId }));
        // Invalidar la caché HTTP del SW para esa carga y para las fotos
        // del detalle — la próxima vez que el cliente pida la lista, no
        // recibirá una respuesta caduca del SW.
        invalidateSwApiCache({ idCargo: Number(idCargo), idCargoDetail: detailId });
      }
      // Si esta edición arrastraba un pending en cola con fotos añadidas
      // offline, ya las hemos subido directamente como parte de newPhotos.
      // Borramos el pending para que la próxima sync no las re-suba.
      // CRÍTICO: solo si NO acabamos de encolar un resto por presupuesto —
      // addPendingDetail fusiona por detalle, así que el resto encolado vive en
      // ESE MISMO id y borrarlo perdería las fotos pendientes.
      if (existingEditPendingId != null && queuedPhotoRemainder === 0) {
        try {
          await deletePendingDetail(existingEditPendingId);
          notifyPendingChanged();
        } catch (e) {
          captureError(e, { where: 'EditDetailPage.deletePendingAfterOnlineSave', pendingId: existingEditPendingId });
        }
      }
      outcome = queuedPhotoRemainder > 0
        ? {
            kind: 'offlinePending',
            message: t('editDetail.budgetPhotosQueued', { count: queuedPhotoRemainder }),
          }
        : { kind: 'success' };
    } catch (err: unknown) {
      const axiosErr = err as { isAxiosError?: boolean; response?: { status?: number; data?: unknown }; message?: string; code?: string };
      const noResponse = !!axiosErr?.isAxiosError && axiosErr.response == null;
      // "Truly offline": red caída de verdad O el usuario está en modo
      // offline manual (en este último caso el interceptor abortó la
      // request, axios devuelve sin response).
      const trulyOffline = noResponse && isOfflineForUser();
      captureError(err, { where: 'EditDetailPage.saveOnline', isNew, trulyOffline });

      // Sin respuesta HTTP = encolar SIEMPRE, también con onLine=true (timeout
      // con red viva — antes moría en "no se pudo leer la respuesta" y obligaba
      // a reintentar a mano; era justo el caso a cerrar). Si el servidor sí
      // procesó pese al timeout, la barrera pre-create de la cola encontrará el
      // detalle por containerNumber y no duplicará.
      if (noResponse) {
        try {
          const editId = isNew ? 0 : (Number(idDetail) || existingDetail?.idCargoDetail || 0);
          await addPendingDetail(
            Number(idCargo),
            isNew ? 'NEW' : 'EDIT',
            { ...payload, idCargoDetail: editId, listNewImages: newPhotos, listDeleteImages: photosToDelete } as Detail
          );
          notifyPendingChanged();
          track({ name: 'detail_create_offline_queued', params: { photo_count: newPhotos.length } });
          outcome = {
            kind: 'offlinePending',
            message: trulyOffline
              ? undefined // mensaje estándar de "sin conexión"
              : t('editDetail.timeoutQueued'),
          };
        } catch (e) {
          captureError(e, { where: 'EditDetailPage.addPendingDetail.noResponse', idCargo });
          outcome = { kind: 'error', message: t('editDetail.errorSaveRetry') };
        }
      } else {
        const status = axiosErr.response?.status ?? '';
        const msg = JSON.stringify(axiosErr.response?.data ?? axiosErr.message ?? err);
        outcome = { kind: 'error', message: t('editDetail.errorSaveGeneric', { status, msg }) };
      }
    }
    }

    // El submit terminó por sí mismo: desarmar el watchdog. Si el watchdog ya
    // actuó (encoló, limpió, navegó), este flujo tardío se retira en silencio —
    // sus efectos de red ya viajaron y son inocuos (dedup).
    clearTimeout(watchdog);
    if (submitAbandoned.current) return;

    // Limpieza del borrador — SIEMPRE el último paso: solo después de que cada
    // foto esté subida o persistida en la cola offline. Un crash ANTES de esta
    // línea deja borrador de más (inocuo: la reconciliación por fileName lo
    // absorbe al reabrir); limpiar antes de persistir fue pérdida real en
    // Android. Con fallos de subida sueltos el borrador SE CONSERVA — esas
    // fotos no están en ningún otro sitio.
    if (outcome && outcome.kind !== 'error' && !unsavedPhotoFailures) {
      // ORDEN CRÍTICO: cerrojo PRIMERO, delete DESPUÉS — ningún efecto en vuelo
      // puede colarse entre medias y resucitar el borrador.
      draftClosedRef.current = true;
      try {
        await deletePhotoDraft(submitDraftKey);
      } catch (e) {
        // Conservar de más es seguro para las fotos, pero un delete fallido EN
        // SILENCIO produce exactamente el bug de campo (fotos resucitando en la
        // siguiente carga nueva) — que se vea en Sentry.
        captureError(e, { where: 'EditDetailPage.deletePhotoDraft', draftKey: submitDraftKey });
      }
      // Defensivo (lección Android): tras un CREATE con éxito, limpiar también
      // la clave compartida de "carga nueva" — cualquier huérfano bajo
      // idCargo:new resucitaría en la siguiente carga nueva del cargo.
      if (isNew && submitDraftKey !== `${idCargo}:new`) {
        try { await deletePhotoDraft(`${idCargo}:new`); } catch { /* best-effort */ }
      }
    } else {
      // Guardado fracasado con fotos SIN encolar: el borrador debe seguir vivo
      // — liberamos el cerrojo por si un intento anterior lo echó.
      draftClosedRef.current = false;
    }

    // Sea cual sea el resultado, si hay fotos locales y galería habilitada,
    // ofrecemos guardarlas en galería ANTES de cerrar/avisar. Así nunca
    // se pierden aunque el upload haya fallado.
    type ShareNav = Navigator & {
      canShare?: (data: { files?: File[] }) => boolean;
      share?: (data: { files?: File[]; title?: string }) => Promise<void>;
    };
    const navWithShare = navigator as ShareNav;
    // Respaldo de las fotos NUEVAS (aún sin subir), en cualquier plataforma.
    // Fuera de iOS la acción es descarga local y no necesita navigator.share;
    // en iOS sí exige el share sheet.
    const canDoShare =
      prefs.galleryEnabled &&
      newPhotos.length > 0 &&
      (!IS_IOS || typeof navWithShare.share === 'function');

    if (canDoShare) {
      setSaving(false);
      setSaveResult(outcome);
      setGalleryPromptOpen(true);
      return;
    }

    setSaving(false);
    if (!outcome) return;
    if (outcome.kind === 'success') {
      navigate(-1);
    } else if (outcome.kind === 'offlinePending') {
      // Por si la red volvió mientras encolábamos, intentamos un flush
      // inmediato — Background Sync lo hará igual cuando detecte la red,
      // pero esto reduce la latencia perceptible al usuario.
      triggerOfflineFlush(true);
      alert(outcome.message ?? t('editDetail.offlineWillSync'));
      navigate(-1);
    } else {
      alert(outcome.message);
    }
  };

  // Acción a ejecutar tras cerrar el modal de galería (o tras guardar a
  // galería). Centralizada para no duplicarla en cada botón del modal.
  const finalizeAfterGalleryPrompt = () => {
    setGalleryPromptOpen(false);
    const out = saveResult;
    setSaveResult(null);
    if (!out) return;
    if (out.kind === 'success') {
      navigate(-1);
    } else if (out.kind === 'offlinePending') {
      triggerOfflineFlush(true);
      alert(out.message ?? t('editDetail.offlineWillSync'));
      navigate(-1);
    } else {
      alert(out.message);
    }
  };

  const handleBackClick = () => {
    const hasChanges = newPhotos.length > 0 ||
      Object.keys(form).some((k) => form[k as keyof Detail] !== (existingDetail ?? EMPTY_DETAIL)[k as keyof Detail]);
    if (hasChanges && !confirm(t('editDetail.confirmExit'))) return;
    navigate(-1);
  };

  const totalPhotos = serverPhotos.length + newPhotos.length;

  return (
    <div className="edit-page">
      <BlockAutoUpdate reason="editing-detail" />
      <AppHeader title={isNew ? t('editDetail.titleNew') : t('editDetail.titleEdit')} showBack />

      <form onSubmit={handleSubmit} className="edit-form">
        {position && (
          <div className="edit-position-banner">
            <div className="edit-position-banner__row">
              <span className="edit-position-banner__booking">{position.bookingPortic}</span>
              <span className="edit-position-banner__product">
                {displayProductNames(position) ?? <DraftProduct text={productDraftOf(position)} />}
              </span>
            </div>
            <span className="edit-position-banner__factory">{position.factoryName}</span>
            {supplierStamp && (
              <span className="edit-position-banner__supplier">
                {t('editDetail.supplierFilled', { who: supplierStamp.who, when: supplierStamp.when })}
              </span>
            )}
          </div>
        )}

        <section className="edit-section">
          <h3 className="edit-section__title">{t('editDetail.sectionId')}</h3>

          {/* Tipo de contenedor primero */}
          <div className="edit-field">
            <label>{t('editDetail.containerType')}</label>
            <select
              value={form.idContainerType ?? 1}
              onChange={(e) => set('idContainerType', Number(e.target.value))}
            >
              {CONTAINER_TYPES.map((ct) => (
                <option key={ct.id} value={ct.id}>{ct.label}</option>
              ))}
            </select>
          </div>

          {/* N.º Contenedor */}
          <div className="edit-field" style={{ gridColumn: '1 / -1' }}>
            <div className="edit-field__label-row">
              <label>{t('editDetail.containerNumber')}</label>
              {isOnline && <OcrBtn onClick={() => startOcr('containerNumber')} />}
            </div>
            <div className="edit-field__row">
              <input
                ref={containerNumberRef}
                value={form.containerNumber ?? ''}
                onChange={(e) => set('containerNumber', e.target.value.toUpperCase())}
                onBlur={handleContainerBlur}
                placeholder={t('editDetail.containerPlaceholder')}
                className={errors.containerNumber ? 'input--error' : ''}
              />
              {containerValid === true && <span className="validation-icon validation-icon--ok">✓</span>}
              {containerValid === false && <span className="validation-icon validation-icon--err">✗</span>}
            </div>
            {errors.containerNumber && <span className="field-error">{errors.containerNumber}</span>}
          </div>

          {/* Matrículas: no aplican al perfil self-managed (ver features.ts).
              Los campos son opcionales en BD (miles de detalles ya guardados sin
              matrícula), así que ocultarlos no rompe el guardado. El OCR va con
              ellas: es lo único que lee. */}
          {features.truckPlate && (
            <div className="edit-grid">
              {/* Placa vehículo */}
              <div className="edit-field">
                <div className="edit-field__label-row">
                  <label>{t('editDetail.vehiclePlate')}</label>
                  {isOnline && <OcrBtn onClick={() => startOcr('numberPlat', 'truck_plate')} />}
                </div>
                <input
                  value={form.numberPlat ?? ''}
                  onChange={(e) => set('numberPlat', e.target.value.toUpperCase())}
                  placeholder="1234ABC"
                  className={errors.numberPlat ? 'input--error' : ''}
                />
                {errors.numberPlat && <span className="field-error">{errors.numberPlat}</span>}
              </div>

              {/* Placa remolque */}
              <div className="edit-field">
                <div className="edit-field__label-row">
                  <label>{t('editDetail.trailerPlate')}</label>
                  {isOnline && <OcrBtn onClick={() => startOcr('trailerNumberPlat', 'trailer_plate')} />}
                </div>
                <input
                  value={form.trailerNumberPlat ?? ''}
                  onChange={(e) => set('trailerNumberPlat', e.target.value.toUpperCase())}
                  placeholder="R-1234ABC"
                />
              </div>
            </div>
          )}

          <div className="edit-grid">
            {/* N.º Precinto */}
            <div className="edit-field">
              <div className="edit-field__label-row">
                <label>{t('editDetail.sealNumber')}</label>
                {isOnline && <OcrBtn onClick={() => startOcr('sealNumber', 'seal_code')} />}
              </div>
              <input
                value={form.sealNumber ?? ''}
                onChange={(e) => set('sealNumber', e.target.value.toUpperCase())}
                placeholder="AL123456"
                className={errors.sealNumber ? 'input--error' : ''}
              />
              {errors.sealNumber && <span className="field-error">{errors.sealNumber}</span>}
            </div>
            <div className="edit-field">
              <label>{t('editDetail.vetSeal')}</label>
              <input
                value={form.sealVeter ?? ''}
                onChange={(e) => set('sealVeter', e.target.value.toUpperCase())}
                placeholder="VET-001"
              />
            </div>
          </div>

          {form.carrierName ? (
            <div className="edit-field">
              <label>{t('editDetail.carrier')}</label>
              <div className="edit-readonly">{form.carrierName}</div>
            </div>
          ) : null}
        </section>

        {prefs.locationEnabled && (
          <section className="edit-section">
            <h3 className="edit-section__title">{t('editDetail.sectionLot')}</h3>
            <div className="edit-field">
              <label>{t('editDetail.lotNumber')}</label>
              <input
                value={form.lotNumber ?? ''}
                onChange={(e) => set('lotNumber', e.target.value)}
                placeholder="LOT-001"
              />
            </div>
            <div className="edit-field">
              <label>{t('editDetail.location')}</label>
              <input
                value={form.location ?? ''}
                onChange={(e) => set('location', e.target.value)}
                placeholder={t('editDetail.locationPlaceholder')}
              />
            </div>
          </section>
        )}

        <section className="edit-section">
          <h3 className="edit-section__title">{t('editDetail.sectionCargo')}</h3>
          <div className="edit-grid">
            <div className="edit-field">
              <label>{t('editDetail.bundles')}</label>
              <input type="number" min="0" step="any" inputMode="decimal"
                value={form.packageNumber ?? ''}
                onChange={setNum('packageNumber')}
                className={errors.packageNumber ? 'input--error' : ''}
              />
              {errors.packageNumber && <span className="field-error">{errors.packageNumber}</span>}
            </div>
            <div className="edit-field">
              <label>{t('editDetail.humidity')}</label>
              <input type="number" min="0" max="100" step="any" inputMode="decimal"
                value={form.humidity ?? ''}
                onChange={setNum('humidity')}
              />
            </div>
            <div className="edit-field">
              <label>{t('editDetail.tare')}</label>
              <input type="number" min="0" step="any" inputMode="decimal"
                value={form.tare ?? ''}
                onChange={setNum('tare')}
              />
            </div>
            <div className="edit-field">
              <label>{t('editDetail.netWeight')}</label>
              <input type="number" min="0" step="any" inputMode="decimal"
                value={form.netWeight ?? ''}
                onChange={setNum('netWeight')}
              />
            </div>
          </div>
        </section>

        <section className="edit-section">
          <h3 className="edit-section__title">{t('editDetail.sectionExit')}</h3>
          <div className="edit-field">
            <div className="edit-field__row">
              <input
                value={form.comments ?? ''}
                onChange={(e) => set('comments', e.target.value)}
                placeholder="HH:mm"
              />
              <button type="button" className="time-refresh-btn" onClick={refreshTime} title={t('editDetail.currentTime')}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                  <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z"/>
                </svg>
              </button>
            </div>
          </div>
        </section>

        {/* ── Fotos ── */}
        <section className="edit-section">
          <div className="photos-header">
            <h3 className="edit-section__title">
              {t('editDetail.photos')} {totalPhotos > 0 && <span className="photos-count">({totalPhotos})</span>}
            </h3>
            <div className="photo-add-btns">
              {!selectMode && (
                <>
                  <button
                    type="button"
                    className="photo-add-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={totalPhotos >= MAX_PHOTOS}
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                      <path d="M12 15.2c1.769 0 3.2-1.431 3.2-3.2S13.769 8.8 12 8.8 8.8 10.231 8.8 12s1.431 3.2 3.2 3.2zM9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9z" />
                    </svg>
                    {t('editDetail.camera')}
                  </button>
                  <button
                    type="button"
                    className="photo-add-btn"
                    onClick={() => galleryInputRef.current?.click()}
                    disabled={totalPhotos >= MAX_PHOTOS}
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                      <path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" />
                    </svg>
                    {t('editDetail.gallery')}
                  </button>
                </>
              )}
              {totalPhotos > 0 && (
                selectMode ? (
                  <button type="button" className="photo-add-btn photo-add-btn--cancel" onClick={cancelSelectMode}>
                    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                  </button>
                ) : (
                  <button type="button" className="photo-add-btn photo-add-btn--select" onClick={() => setSelectMode(true)}>
                    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                    </svg>
                    {t('editDetail.selectToDelete')}
                  </button>
                )
              )}
            </div>
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handlePhotoPick} />
          <input ref={galleryInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handlePhotoPick} />

          {/* OCR file inputs (hidden) */}
          <input ref={ocrCameraRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleOcrFilePick} />
          <input ref={ocrGalleryRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleOcrFilePick} />

          {loadingPhotos && (
            <div className="photos-loading">
              <div className="spinner spinner--green" />
              <span>{t('editDetail.loadingPhotos')}</span>
            </div>
          )}

          {(serverPhotos.length > 0 || newPhotos.length > 0) && (
            <div className="photo-grid">
              {serverPhotos.map((p, i) => (
                <div
                  key={`server-${i}`}
                  className={`photo-thumb photo-thumb--server ${selectMode && selectedServer.has(i) ? 'photo-thumb--selected' : ''}`}
                  onClick={() => selectMode ? toggleSelectServer(i) : setLightbox(p.urlImage)}
                >
                  <AuthImage src={p.urlImage} alt={p.imageName} className="photo-thumb__img" />
                  {selectMode && (
                    <div className={`photo-check ${selectedServer.has(i) ? 'photo-check--on' : ''}`}>
                      {selectedServer.has(i) && (
                        <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                        </svg>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {newPhotos.map((p, i) => (
                <div
                  key={`new-${i}`}
                  className={`photo-thumb photo-thumb--new ${selectMode && selectedNew.has(i) ? 'photo-thumb--selected' : ''}`}
                  onClick={() => selectMode ? toggleSelectNew(i) : setLightbox(p.urlImage)}
                >
                  <img src={p.urlImage} alt={p.imageName} className="photo-thumb__img" />
                  {selectMode ? (
                    <div className={`photo-check ${selectedNew.has(i) ? 'photo-check--on' : ''}`}>
                      {selectedNew.has(i) && (
                        <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                        </svg>
                      )}
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="photo-remove"
                      onClick={(e) => { e.stopPropagation(); removeNewPhoto(i); }}
                    >✕</button>
                  )}
                </div>
              ))}
            </div>
          )}

          {selectMode && (
            <div className="photo-select-bar">
              <span className="photo-select-count">
                {totalSelected > 0 ? t('editDetail.selectedCount', { count: totalSelected }) : t('editDetail.tapToSelect')}
              </span>
              <button
                type="button"
                className="photo-delete-btn"
                disabled={totalSelected === 0}
                onClick={deleteSelected}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                </svg>
                {t('editDetail.delete')}{totalSelected > 0 ? ` (${totalSelected})` : ''}
              </button>
            </div>
          )}
        </section>

        {/* ── Documento de transporte (DDT): solo proveedor y admin (2026-10-23) ── */}
        {features.deliveryNote && (
          <DeliveryNoteSection
            idCargoDetail={isNew || isEditingPendingNew ? undefined : (Number(idDetail) || existingDetail?.idCargoDetail || undefined)}
            idCargo={Number(idCargo)}
            isOnline={isOnline}
          />
        )}

        <div className="edit-actions">
          <button type="button" className="btn btn--secondary" onClick={handleBackClick}>
            {t('editDetail.cancel')}
          </button>
          <button type="submit" className="btn btn--primary" disabled={saving}>
            {saving ? (
              <>
                <span className="btn-spinner" aria-hidden="true" />
                <span>{t('editDetail.saving')}</span>
              </>
            ) : (
              t('editDetail.save')
            )}
          </button>
        </div>
      </form>

      {/* Lightbox con pinch-zoom y pan */}
      {lightbox && (
        <PhotoLightbox url={lightbox} onClose={() => setLightbox(null)} />
      )}

      {/* OCR Modal */}
      {ocr && (
        <OcrModal
          ocr={ocr}
          onClose={handleOcrClose}
          onContainerChoice={handleContainerChoice}
          onSourcePick={handleOcrSourcePick}
          onRepeat={handleOcrRepeat}
          onAccept={handleOcrAccept}
          onEditText={handleOcrTextChange}
        />
      )}

      {/* Toast */}
      {limitMsg && (
        <div className="photo-limit-overlay" onClick={() => setLimitMsg('')}>
          <div className="photo-limit-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="photo-limit-dialog__icon">
              <svg viewBox="0 0 24 24" fill="currentColor" width="36" height="36">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
              </svg>
            </div>
            <h3 className="photo-limit-dialog__title">{t('editDetail.photoLimitTitle')}</h3>
            <p className="photo-limit-dialog__desc">{limitMsg}</p>
            <button type="button" className="photo-limit-dialog__btn" onClick={() => setLimitMsg('')}>
              {t('editDetail.understood')}
            </button>
          </div>
        </div>
      )}

      {/* Modal post-guardado: ofrecer guardar fotos en galería.
          Aparece sea cual sea el resultado del intento de subida — así
          el usuario nunca pierde sus fotos locales aunque el upload haya
          fallado por red o por error del servidor. */}
      {galleryPromptOpen && (
        <div className="photo-limit-overlay">
          <div className="photo-limit-dialog" onClick={(e) => e.stopPropagation()}>
            <div
              className="photo-limit-dialog__icon"
              style={
                saveResult?.kind === 'error'
                  ? { background: 'var(--orange-bg-soft)', color: 'var(--orange-text-2)' }
                  : { background: 'var(--brand-bg-tint-3)', color: 'var(--brand-primary)' }
              }
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="36" height="36">
                <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
              </svg>
            </div>
            <h3 className="photo-limit-dialog__title">{t('editDetail.galleryTitle')}</h3>
            <p className="photo-limit-dialog__desc">
              {saveResult?.kind === 'success'
                ? t('editDetail.gallerySuccess')
                : saveResult?.kind === 'offlinePending'
                ? t('editDetail.galleryOffline')
                : t('editDetail.galleryError')}
              {IS_ANDROID
                ? <> {t('editDetail.galleryAndroidHint')}</>
                : !IS_IOS && <> {t('editDetail.galleryDesktopHint')}</>}
            </p>
            <button
              type="button"
              className="photo-limit-dialog__btn"
              onClick={async () => {
                // Respaldo de las fotos NUEVAS (aún sin subir).
                try {
                  if (IS_IOS) {
                    // iOS: share sheet → app Fotos.
                    const files = newPhotos
                      .map((p) => {
                        const b = newPhotoToBlob(p);
                        return b ? new File([b], p.imageName || 'foto.jpg', { type: 'image/jpeg' }) : null;
                      })
                      .filter((f): f is File => f !== null);
                    type ShareNav = Navigator & {
                      canShare?: (data: { files?: File[] }) => boolean;
                      share?: (data: { files?: File[]; title?: string }) => Promise<void>;
                    };
                    const nav = navigator as ShareNav;
                    if (files.length && nav.canShare?.({ files }) && nav.share) {
                      await nav.share({ files, title: t('editDetail.shareTitle') });
                    }
                  } else {
                    // Android + escritorio: descarga local. Sin toast propio:
                    // la pantalla puede navegar justo después y el feedback lo
                    // dan las notificaciones de descarga del navegador.
                    await downloadPhotosLocally(newPhotos);
                  }
                } catch {
                  // Usuario canceló la hoja o error; ignoramos.
                }
                finalizeAfterGalleryPrompt();
              }}
            >
              {t(IS_IOS ? 'editDetail.saveToGallery' : 'editDetail.saveToDevice')}
            </button>
            <button
              type="button"
              className="photo-limit-dialog__btn"
              style={{ background: 'var(--bg-button-ghost)', color: 'var(--text-tertiary)', marginTop: 8 }}
              onClick={finalizeAfterGalleryPrompt}
            >
              {t('editDetail.noThanks')}
            </button>
          </div>
        </div>
      )}

      {toast && (
        <div className="toast" onClick={() => setToast('')}>{toast}</div>
      )}
    </div>
  );
}
